import { FC, useContext, useEffect, useState, useLayoutEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";

import AuthSubHeader from "src/components/authSubHeader";
import FooterText from "src/components/footerText";
import CaseStepOne from "src/components/caseStepOne";
import CaseStepTwo from "src/components/caseStepTwo";
import CaseStepThree from "src/components/caseStepThree";
import RestoreCaseSessionModal from "./modal";
import PatientLookupModal from "src/components/addCase/lookupPatientModal";
import PendingSubmissions from "src/components/addCase/pendingSubmissions";

import { AddCaseSchema } from "src/validation/addCase";
import { populatingCloneCase } from "src/helpers/cloneCase";
import { CaseContext } from "src/context/caseContext";
import { AuthContext } from "src/context/authContext";
import { ToastContext } from "src/context/toastContext";
import { LoaderContext } from "src/context/loaderContext";

import useFetch from "src/hooks/useFetch";
import { useDraftCaseFunc } from "src/hooks/useDraftCaseFunc";
import { usePageTrackingAndStorage } from "src/hooks/usePageTrackingAndStorage";
import useCaseCreateCall from "src/hooks/useCaseCreateCall";
import usePatientLookupCall from "src/hooks/usePatientLookupCall";

import { ToastTypes } from "src/types";
import { UserType } from "src/types/users";
import { APIEndpoints } from "src/types/apiTypes";
import { CaseClonePathParams, CaseCloneResponseType } from "./types";
import { AddCaseProps, PatientAttributes } from "src/pages/addCase/types";
import { CASE_FORM_DEFAULT_VALUES } from "src/constants/cases";



const HubCaseClone: FC = (): JSX.Element => {
  const [step, changeStep] = useState<number>(1);
  const [isStepTwoMounted, mountingStepTwo] = useState<boolean>(false);
  const [isModalVisible, showHideModal] = useState<boolean>(false);
  const [isStepOneInvalid, setStepOneValidity] = useState<boolean>(false);
  const [isStepTwoInvalid, setStepTwoValidity] = useState<boolean>(false);
  const [isDuplicateModalVisible, setDuplicateModalVisibility] = useState<boolean>(false)
  const [patientPrevCaseDate, updatePatientPrevCaseDate] = useState<string>("")
  const [isSubmitClicked] = useState<boolean>(false);

  const { userData } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);
  const { draftCaseId, updateCaseDraftStatus } = useContext(CaseContext)
  const { saveDraftCase, saveDraftLoader, updateDraftLoader } = useDraftCaseFunc()
  const { setLoader, hideLoader } = useContext(LoaderContext)
  const navigate = useNavigate()
  const { caseId } = useParams();
  const [selectedDoctorObj, setSelectedDoctorObj] = useState<any | null>(null);

  const { control, formState, handleSubmit, getValues, clearErrors, watch, trigger, setValue, reset ,resetField} = useForm<AddCaseProps>({
    mode: "all",
    resolver: yupResolver(AddCaseSchema),
    defaultValues: {
      ...CASE_FORM_DEFAULT_VALUES,
      user_id: userData ? userData.id : 0,
      creator_name: userData ? userData.name : "",
    }
  });

  usePageTrackingAndStorage(getValues, formState.isDirty, step)

  const { addCaseLoading, isUploading, caseDataSubmission } = useCaseCreateCall(getValues)
  const { lookupForPatientName, totalRefills } = usePatientLookupCall(updatePatientPrevCaseDate, setDuplicateModalVisibility, caseDataSubmission)

  const patientData = watch("patient_attributes") as PatientAttributes;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => setLoader(), [])


  useEffect(() => {
    window.addEventListener('unload', () => {
      if (formState.isDirty)
        localStorage.setItem('newCase', JSON.stringify({ newData: getValues(), step }));
    });

    const data = localStorage.getItem('newCase');
    if (data) showHideModal(true)

    return () => {
      window.removeEventListener('unload', () => {});
      if (formState.isDirty)
        localStorage.setItem('newCase', JSON.stringify({ newData: getValues(), step }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (patientData && patientData.global_patient_id)
      trigger(["patient_attributes", "insurances_attributes"]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientData]);


  const uploadedDocs = watch("uploaded_documents");
  const paApproved = watch("case_attributes.pa_approved");

  useEffect(() => {
    if (uploadedDocs && uploadedDocs.length > 0 && (formState.errors && formState.errors.uploaded_documents))
      trigger("uploaded_documents")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedDocs])

  useEffect(() => {
    if (paApproved && paApproved.length > 0 && (formState.errors && formState.errors.uploaded_documents)) {
      clearErrors("uploaded_documents")
    }

    if (paApproved !== "pa_approved") setValue("case_attributes.pa_approved_date", "")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paApproved])





  const { error: caseCloneError, data: caseCloneData, loading: caseCloneLoading } =
    useFetch<CaseCloneResponseType, {}, CaseClonePathParams>(APIEndpoints.CLONE_CASE_DETAILS, {}, { caseId: caseId ? Number(caseId) : 0 });

  useEffect(() => {
    if (caseCloneLoading === true) setLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseCloneLoading]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (caseCloneData || caseCloneError) {
      hideLoader();
      if (caseCloneData) {
        timeout = setTimeout(() => {
          const caseFormData = populatingCloneCase(caseCloneData, userData || {} as UserType, getValues);
          reset(caseFormData);
        }, 1500);
      }
    }
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseCloneData, caseCloneError]);





  useEffect(() => {
    const { errors } = formState;

    if (Object.keys(errors).length > 0) {
      if (Object.hasOwn(errors, 'prescription_attributes'))
        setStepOneValidity(true);
      else
        setStepOneValidity(false);

      if (Object.hasOwn(errors, 'patient_attributes') ||
        Object.hasOwn(errors, 'case_doctor') ||
        Object.hasOwn(errors, 'insurances_attributes') ||
        Object.hasOwn(errors, "uploaded_documents") ||
        Object.hasOwn(errors, "case_attributes")
      )
        setStepTwoValidity(true);
      else
        setStepTwoValidity(false);
    } else {
      setStepOneValidity(false);
      setStepTwoValidity(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState]);

  useEffect(() => {
    const messages = [];
    const { errors } = formState;

    if (isStepOneInvalid && isStepTwoInvalid && step >= 2) {
      messages.push(`The following sections from step 1 have errors:
      ${errors.prescription_attributes ? '- case prescription<br>' : ''}`);

      messages.push(`The following sections from step 2 have errors: <br>
      ${errors.doctor_attributes ? '- case doctor<br>' : ''}
      ${errors && errors.patient_attributes ? '- case patient<br>' : ''}
      ${errors.patient_attributes && Object.hasOwn(errors.patient_attributes, "phone_numbers") ? '- case patient phone numbers<br>' : ''}
      ${errors.patient_attributes && Object.hasOwn(errors.patient_attributes, "address") ? '- case patient address<br>' : ''}
      ${errors.insurances_attributes ? '- case patient insurances' : ''}
      ${errors.uploaded_documents ? '- case documents<br>' : ''}
      ${errors.case_attributes ? '- case attributes<br>' : ''}`);
    }
    else if (isStepOneInvalid && step >= 1) {
      messages.push(`The following sections from step 1 have errors:
      ${errors.prescription_attributes ? '- case prescription<br>' : ''}`);
    }
    else if (isStepTwoInvalid && step >= 2) {
      messages.push(`The following sections from step 2 have errors: <br>
      ${errors.doctor_attributes ? '- case doctor<br>' : ''}
      ${errors && errors.patient_attributes ? '- case patient<br>' : ''}
      ${errors.patient_attributes && Object.hasOwn(errors.patient_attributes, "phone_numbers") ? '- case patient phone numbers<br>' : ''}
      ${errors.patient_attributes && Object.hasOwn(errors.patient_attributes, "address") ? '- case patient address<br>' : ''}
      ${errors.insurances_attributes ? '- case patient insurances' : ''}
      ${errors.uploaded_documents ? '- case documents<br>' : ''}
      ${errors.case_attributes ? '- case attributes<br>' : ''}`);
    }

    showToast(messages, ToastTypes.WARNING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStepOneInvalid, isStepTwoInvalid, step]);


  const onStepProgressionClick = (localStep: number) => {
    // trigger([ "prescription_attributes", "case_attributes", "uploaded_documents"]);
    if (step === 1)
      trigger(["prescription_attributes"]);
    else if (step === 2)
      trigger(["doctor_attributes", "patient_attributes", "insurances_attributes", "uploaded_documents", "case_attributes"]);

    if (localStep >= 2)
      mountingStepTwo(true);
    else if (localStep === 3)
      trigger([ "prescription_attributes", "case_attributes", "doctor_attributes", "patient_attributes", "insurances_attributes", "uploaded_documents"]);

    changeStep(localStep);

    //:- logic to save case data in localstorage
    // localStorage.setItem('newCase', JSON.stringify({ newData: getValues(), step }));
  };

  const resetCaseForm = (caseData?: Partial<AddCaseProps>) => {
    if (caseData) reset(caseData)
    else reset({
      ...CASE_FORM_DEFAULT_VALUES,
      user_id: userData ? userData.id : 0,
      creator_name: userData ? userData.name : "",
      doctor_attributes: {
        global_doctor_id: 0,
        practice_id: 0,
      }
    })
  }

  const onCaseSubmission = (val: AddCaseProps) => {

      const { firstname, middlename, lastname } = val.patient_attributes as PatientAttributes;
      lookupForPatientName(firstname, lastname, middlename)
      updateCaseDraftStatus(true)

  }


  const saveDraftAndClearDocument = () => {
    setValue("uploaded_documents",[])
    saveDraftCase(getValues())
  }


  return (
    <div className="d-flex flex-fill overflow-auto">
      <div className="container d-flex flex-column" style={{ maxWidth: '90rem' }}>
        <AuthSubHeader/>

        <div className="row g-3 prescription-form">
          <div className="col-lg-2 col-12 pendings-form">
            <div className="d-none d-lg-block" style={{ minHeight: '52px' }}></div>
            <PendingSubmissions reset={resetCaseForm}/>
          </div>

          <form className="col-lg-10 col-12 d-flex flex-column flex-fill start-form" onSubmit={handleSubmit(onCaseSubmission)}>
            <div className="sticky-top z-2 pb-3">
              <div id="case-creation-progress-bar" className="case-progress-bar">
                <button type="button" id="progress-step-1"
                        className={`btn col-4 case-creation-progress-step ${step >= 1 ? isStepOneInvalid ? 'invalid-progress-step' : 'active-progress-step' : ''}`}
                        onClick={() => onStepProgressionClick(1)}
                >
                  1. Getting Started <i className={`bi bi-play-fill progression-arrow-head`}></i>
                </button>

                <button type="button" id="progress-step-2"
                        className={`btn col-4 case-creation-progress-step ${step >= 2 ? isStepTwoInvalid ? 'invalid-progress-step' : 'active-progress-step' : ''}`}
                        onClick={() => onStepProgressionClick(2)}
                >
                  2. Patient Information <i className={`bi bi-play-fill progression-arrow-head`}></i>
                </button>

                <button type="button" id="progress-step-3"
                        className={`btn col-4 case-creation-progress-step ${step >= 3 ? 'active-progress-step' : ''}`}
                        onClick={() => onStepProgressionClick(3)}
                >
                  3. Finish
                </button>
              </div>
            </div>

            <Controller name="original_id" control={control} defaultValue={Number(caseId)} render={() => <></>}/>

            {userData ? (
                <>
                  <Controller
                      name="creator_name"
                      control={control}
                      defaultValue={userData.name}
                      render={() => (<></>)}
                  />

                  <Controller
                      name="user_id"
                      control={control}
                      defaultValue={userData.id}
                      render={() => (<></>)}
                  />
                </>
            ) : (<></>)}

            <div id="step-1" className="case-creation-step">
              {step >= 1 && caseCloneData &&
                  <CaseStepOne control={control} watch={watch} setValue={setValue} getValues={getValues}
                               isVisible={step === 1} resetField={resetField} iscloneCase={true} />}
            </div>
            <div id="step-2" className="case-creation-step">
              {isStepTwoMounted &&
                  <CaseStepTwo resetField={resetField} control={control} getValues={getValues} watch={watch} reset={reset}
                               setValue={setValue} isVisible={step === 2} trigger={trigger}
                              //  setSelectedDoctorObj={setSelectedDoctorObj}
                      />}
            </div>
            <div id="step-3" className="case-creation-step">
              {step === 3 && <CaseStepThree getValues={getValues} control={control} selectedDoctorObj={selectedDoctorObj} submitClicked = {isSubmitClicked}
              />}
            </div>

            <div className="d-flex gap-2 mt-2 sticky-bottom z-0 bg-white py-2 px-3 p-md-3 border rounded-2" style={{ boxShadow: '0 0 15px #CCC' }}>
              <button
                  type="button" key="previousStepButton"
                  className={`btn btn-outline-secondary ${step === 1 ? 'd-none' : ''}`}
                  onClick={() => onStepProgressionClick(step - 1)}
              >
                <i className="bi bi-chevron-double-left"></i> Previous
              </button>

              {step === 3 ? (
                  <>
                    <button
                        className="btn btn-primary"
                        disabled={
                            (!(patientData.firstname?.length > 0 && patientData.lastname?.length > 0))
                            || saveDraftLoader
                            || (draftCaseId !== 0 && updateDraftLoader)
                        }
                        type="button"
                        onClick={() => saveDraftAndClearDocument()}
                    >
                      {saveDraftLoader || (draftCaseId !== 0 && updateDraftLoader) ? (
                          <i className="text-white me-2 spinner-border" style={{ width: `24px`, height: `24px`, fontSize: "10px" }}></i>
                      ) : (
                          <i className="bi bi-files"></i>
                      )}
                      {' '}
                      {draftCaseId === 0 ? "Save as Draft" : "Update Draft"}
                    </button>
                    <button
                        type="submit" disabled={addCaseLoading || isUploading}
                        key="formSubmitButton"
                        className="d-flex btn btn-primary"
                    >
                      {addCaseLoading || isUploading ? (
                          <i className="text-white me-2 spinner-border" style={{ width: `24px`, height: `24px`, fontSize: "10px" }}></i>
                      ) : null} <i className="bi bi-send-fill"></i>
                      <span className="d-block d-lg-inline-block">Submit Request</span>
                    </button>
                  </>
              ) : (
                  <button
                      type="button" key="nextStepButton"
                      className="btn btn-outline-secondary"
                      onClick={() => onStepProgressionClick(step + 1)}
                  >
                    Next <i className="bi bi-chevron-double-right"></i>
                  </button>
              )}

              {step !== 3 && (
                <button
                    className="btn btn-primary"
                    disabled={
                        (!(patientData.firstname?.length > 0 && patientData.lastname?.length > 0))
                        || saveDraftLoader
                        || (draftCaseId !== 0 && updateDraftLoader)
                    }
                    type="button"
                    onClick={() => saveDraftAndClearDocument()}
                >
                  {saveDraftLoader || (draftCaseId !== 0 && updateDraftLoader) ? (
                      <i className="text-white me-2 spinner-border" style={{ width: `24px`, height: `24px`, fontSize: "10px" }}></i>
                  ) : (
                      <i className="bi bi-files me-2"></i>
                  )}
                  {' '}
                  {draftCaseId === 0 ? "Save as Draft" : "Update Draft"}
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="bg-white mt-3"><FooterText footerType="black"/></div>
      </div>
      <RestoreCaseSessionModal
        isModalVisible={isModalVisible}
        removeCurrentData={() => showHideModal(false)}
        restoringCaseData={() => {
          showHideModal(false);
          navigate("/cases/new", { state: { fromClone: true } })
        }}
      />
      <PatientLookupModal
        isModalVisible={isDuplicateModalVisible}
        patientPrevCaseDate={patientPrevCaseDate}
        totalRefills={totalRefills}
        caseDataSubmission={() => {
          updateCaseDraftStatus(true)
          setDuplicateModalVisibility(false)
          caseDataSubmission()
        }}
        clearCaseData={() => {
          setDuplicateModalVisibility(false)
          navigate("/cases")
        }}
      />
    </div>
  );
};

export default HubCaseClone;
